import { xmlElementPath } from '../xml';
import type { FieldSpec } from './types';

function selectorHits(field: FieldSpec, clicked: string): boolean {
  return field.xmlSelectors.some((s) => s === clicked);
}

function descendantHits(field: FieldSpec, clicked: string): boolean {
  const el = xmlElementPath(clicked);
  if (clicked.includes('@')) return false;
  return field.xmlSelectors.some((s) => xmlElementPath(s).startsWith(`${el}/`));
}

export function createFieldIndex(fields: FieldSpec[]) {
  const byKey = new Map(fields.map((f) => [f.key, f]));

  function highlightSelectors(key: string): string[] {
    return byKey.get(key)?.xmlSelectors ?? [];
  }

  function resolveXmlClick(
    selector: string,
    expert: boolean,
  ): { field: FieldSpec; enableExpert: boolean } | null {
    const exact = fields.filter((f) => selectorHits(f, selector));
    const pool = exact.length > 0 ? exact : fields.filter((f) => descendantHits(f, selector));
    if (pool.length === 0) return null;
    if (exact.length === 0 && pool.length > 1) return null;

    const preferred =
      pool.find((f) => (expert ? true : !f.expertOnly) && f.kind !== 'checkbox') ??
      pool.find((f) => (expert ? true : !f.expertOnly)) ??
      pool[0];

    if (!expert && preferred.expertOnly) {
      if (preferred.simpleAlias) {
        const alias = byKey.get(preferred.simpleAlias);
        if (alias) return { field: alias, enableExpert: false };
      }
      return { field: preferred, enableExpert: true };
    }

    if (expert && pool.length > 1) {
      const dedicated = pool.find((f) => f.kind !== 'checkbox') ?? preferred;
      return { field: dedicated, enableExpert: false };
    }

    return { field: preferred, enableExpert: false };
  }

  function fieldByKey(key: string): FieldSpec | undefined {
    return byKey.get(key);
  }

  return { highlightSelectors, resolveXmlClick, fieldByKey };
}
